import { trackGreenScreenInvitationShown } from "@design-lab/shared";
import { BACKGROUND_TYPE } from "@design-lab/shared/constants";
import {
  useShowDesktopFinalVerification,
  useShowFinalVerificationMobileVariant1
} from "@design-lab/shared/lib/config";
import { artboardBackgroundAtom } from "@design-lab/widgets/artboard/model/state";
import { useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";
import {
  greenScreenVerificationModalOpenAtom,
  greenScreenVerificationSeenAtom,
  STORAGE_KEY
} from "./state";

type ShowInvitationOptions = {
  currentTab?: string;
  targetTab?: string;
  isEditMode?: boolean;
};

export function useGreenScreenVerification() {
  const shouldShowVerification = useShowDesktopFinalVerification();
  const shouldShowVerificationMobile = useShowFinalVerificationMobileVariant1();
  const [verificationSeen, setVerificationSeen] = useAtom(greenScreenVerificationSeenAtom);
  const [modalOpen, setModalOpen] = useAtom(greenScreenVerificationModalOpenAtom);
  const currentBackground = useAtomValue(artboardBackgroundAtom);

  // Persist verification seen state to localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, String(verificationSeen));
    }
  }, [verificationSeen]);

  /**
   * Intercepts add to cart action to show verification modal
   * @returns true if should proceed with add to cart, false if should show modal
   */
  const interceptAddToCart = (): boolean => {
    if (
      !shouldShowVerification ||
      verificationSeen ||
      currentBackground === BACKGROUND_TYPE.GREEN
    ) {
      return true;
    }

    setModalOpen(true);
    setVerificationSeen(true);
    return false;
  };

  /**
   * Shows green screen invitation modal with optional conditions for mobile
   * @param options - Configuration options for showing the invitation
   * @returns true if modal was shown, false otherwise
   */
  const showInvitation = (options: ShowInvitationOptions = {}): boolean => {
    const { currentTab, targetTab, isEditMode = false } = options;
    const isCorrectTab = targetTab ? currentTab === targetTab : true;

    if (
      shouldShowVerificationMobile &&
      isCorrectTab &&
      !verificationSeen &&
      currentBackground !== BACKGROUND_TYPE.GREEN &&
      !isEditMode
    ) {
      setModalOpen(true);
      setVerificationSeen(true);
      trackGreenScreenInvitationShown();
      return true;
    }
    return false;
  };

  return {
    shouldShowVerification,
    verificationSeen,
    setVerificationSeen,
    modalOpen,
    setModalOpen,
    interceptAddToCart,
    showInvitation
  };
}
